import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { getTranslation } from 'common/react/utils/translations';
import { UPDATE_NAV_ITEM } from 'store/modules/competition-nav/actions';
import { useDispatch, useSelector } from 'react-redux';
import { navSelector } from 'store/modules/competition-nav/selectors';

/**
 * @param {object} props - Props.
 * @param {React.ReactNode} props.children - Child filter components to render inside the filter section.
 * @param {boolean} props.hideReset - If true, hides the reset button.
 * @param {Function} props.isFiltersVisibleHandler - Handler to toggle filter visibility.
 * @param {Function} [props.setResetFnsProp] - Optional setter for reset functions, passed from parent.
 * @param {string} props.resetText - Text to display on the reset button.
 * @param {Array<string>} [props.filterKeys] - Keys to check for existing filters.
 * @returns {React.ReactElement} Component.
 */
export const StatsSectionNavFilterItems = ({
    children,
    hideReset = false,
    isFiltersVisibleHandler,
    resetText,
    setResetFnsProp,
    filterKeys = []
}) => {
    const [resetFns, setResetFns] = useState([]);
    const nav = useSelector(navSelector);
    const dispatch = useDispatch();
    const initialCompetitionId = nav?.competition?.initialItemId || '';
    const initialSeasonId = nav?.season?.initialItemId || '';
    const isDefaultCompetition = nav?.competition?.id === initialCompetitionId;
    const isDefaultSeason = nav?.season?.id === initialSeasonId;
    const isDefaultHomeAway = nav?.homeAway === 'all';

    const addOnResetCallback = (resetFn) => {
        if (setResetFnsProp) {
            setResetFnsProp((fns) => [...fns, resetFn]);
        } else {
            setResetFns((fns) => [...fns, resetFn]);
        }
    };

    const resetHandler = (resetFunctions) => {
        resetFunctions.forEach((func) => func());
        isFiltersVisibleHandler();

        if (!isDefaultCompetition) {
            dispatch(
                UPDATE_NAV_ITEM.request({
                    navItem: 'competition',
                    value: {
                        id: initialCompetitionId
                    }
                })
            );
        }
        if (!isDefaultSeason) {
            dispatch(
                UPDATE_NAV_ITEM.request({
                    navItem: 'season',
                    value: {
                        id: initialSeasonId
                    }
                })
            );
        }
        if (!isDefaultHomeAway) {
            dispatch(
                UPDATE_NAV_ITEM.request({
                    navItem: 'homeAway',
                    value: 'all'
                })
            );
        }
    };

    const hasAnyFilters = () => {
        const filters = nav?.filters || {};
        return (
            filterKeys.some(
                (key) => Array.isArray(filters[key]) && filters[key].length > 0
            ) ||
            !isDefaultCompetition ||
            !isDefaultSeason ||
            !isDefaultHomeAway
        );
    };

    return (
        <>
            {children && (
                <div className="stats-leaders-filters">
                    {React.Children.map(children, (child) => {
                        if (child) {
                            return (
                                <div className="stats-leaders-filters__item-wrapper">
                                    {React.cloneElement(child, {
                                        addOnResetCallback
                                    })}
                                </div>
                            );
                        }
                        return null;
                    })}
                    {!hideReset && (
                        <div className="stats-leaders-filters__item-wrapper stats-leaders-filters__item-wrapper--reset">
                            {hasAnyFilters() ? (
                                <button
                                    className="stats-leaders-filter-reset-button"
                                    onClick={() =>
                                        resetHandler(
                                            setResetFnsProp
                                                ? setResetFnsProp
                                                : resetFns
                                        )
                                    }
                                >
                                    {resetText
                                        ? resetText
                                        : getTranslation(
                                              'label.statsLeaders.filters.resetButtonText'
                                          )}
                                </button>
                            ) : null}
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

StatsSectionNavFilterItems.defaultProps = {
    name: 'StatsSectionNavFilterItems'
};

StatsSectionNavFilterItems.propTypes = {
    children: PropTypes.any,
    hideReset: PropTypes.bool,
    isFiltersVisibleHandler: PropTypes.func,
    resetText: PropTypes.string,
    setResetFnsProp: PropTypes.func,
    filterKeys: PropTypes.arrayOf(PropTypes.string)
};
